export type ExportQuality = 'best' | '1080p' | '720p';

export const EXPORT_QUALITY_PRESETS: Record<ExportQuality, {
  maxWidth: number | null;
  maxHeight: number | null;
  crf: number;
  preset: string;
  audioBitrate: string;
}> = {
  best:    { maxWidth: null, maxHeight: null, crf: 18, preset: 'medium', audioBitrate: '320k' },
  '1080p': { maxWidth: 1920, maxHeight: 1080, crf: 20, preset: 'medium', audioBitrate: '192k' },
  '720p':  { maxWidth: 1280, maxHeight: 720,  crf: 23, preset: 'fast',   audioBitrate: '128k' },
};

export type IngestStatus = 'pending' | 'uploading' | 'generating-proxy' | 'ready' | 'error';

export interface MediaAsset {
  id: string;
  filename: string;
  mimeType: string;
  /** R2 URL of the original file */
  originalUrl: string;
  /** Duration in milliseconds */
  durationMs: number;
  width: number;
  height: number;
  fps: number;
  ingestStatus: IngestStatus;
  ingestProgress: number; // 0-100
  ingestError?: string;
  /** IDB key for proxy video blob */
  proxyKey?: string;
  /** IDB key for waveform peaks */
  waveformKey?: string;
  /** Media type — 'video' for imported videos, 'audio' for recordings */
  mediaType?: 'video' | 'audio';
  /** SHA-256 hash of original file for deduplication */
  contentHash?: string;
}

// --- Fade types (Phase 4) ---

export type FadeType = 'linear' | 'exponential' | 'logarithmic' | 'sCurve';

export interface FadeConfig {
  durationMs: number;
  type: FadeType;
}

// --- Effects types (Phase 5) ---

export interface EffectInstance {
  id: string;
  type: string;
  params: Record<string, number>;
  bypassed: boolean;
}

// --- Effect Clips (Region-based Event FX) ---

export interface EffectClip {
  id: string;
  trackId: string;
  startMs: number;
  durationMs: number;
  effectType: string;        // key from EFFECT_CATALOG
  params: Record<string, number>;
  bypassed: boolean;
}

export interface Clip {
  id: string;
  assetId: string;
  trackId: string;
  /** Start position on timeline in ms */
  startMs: number;
  /** Duration on timeline in ms */
  durationMs: number;
  /** Offset into the source media in ms */
  sourceOffsetMs: number;
  /** Fade in configuration */
  fadeIn?: FadeConfig;
  /** Fade out configuration */
  fadeOut?: FadeConfig;
}

export interface Track {
  id: string;
  name: string;
  type: 'video' | 'audio' | 'effect';
  clips: Clip[];
  muted: boolean;
  locked: boolean;
  /** Linear volume 0-1 (Phase 3) */
  volume: number;
  /** Pan -1 to 1 (Phase 3) */
  pan: number;
  /** Solo state (Phase 3) */
  soloed: boolean;
  /** Per-track effects chain (Phase 5) */
  effects?: EffectInstance[];
  /** Effect clips — only on effect tracks */
  effectClips?: EffectClip[];
  /** Links effect track to its parent audio track */
  parentTrackId?: string;
}

export interface EditorProject {
  id: string;
  name: string;
  tracks: Track[];
  assets: MediaAsset[];
  durationMs: number;
  fps: number;
  /** Project canvas aspect ratio (width / height). E.g. 16/9, 9/16, 1 */
  projectAspect: number;
  createdAt: number;
  updatedAt: number;
  /** Master effects chain (Phase 5) */
  masterEffects?: EffectInstance[];
}

// --- Project persistence types ---

export interface SavedProject {
  schemaVersion: number;
  project: EditorProject;
  savedAt: number;
}

export interface ProjectIndexEntry {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  thumbnailUrl: string | null;
  durationMs: number;
  trackCount: number;
}

export interface TimelineViewState {
  /** Pixels per millisecond */
  pxPerMs: number;
  /** Horizontal scroll offset in ms */
  scrollOffsetMs: number;
  /** Selected clip id */
  selectedClipId: string | null;
}

export interface PlayheadState {
  currentTimeMs: number;
  playing: boolean;
}
